import React from 'react';
import { useTranslation } from 'react-i18next';

type AccommodationItem = {
    id: string;
    name: string;
    stars?: string;
    description: string;
    contact?: string;
    link?: string;
};

const accommodations: AccommodationItem[] = [
    {
        id: "los_llanos",
        name: "HOTEL LOS LLANOS",
        stars: "****",
        description: "Right in the middle of town, very good restaurant underneath. Get 10% discount when booking by phone or email and using the confirmation number 10627.",
        contact: "reservas@hotellosllanos.es",
        link: "https://www.sercotelhoteles.com/en/hotel-sercotel-los-llanos/"
    },
    {
        id: "eurostars",
        name: "EUROSTARS GRAN HOTEL",
        stars: "****",
        description: "One of the most beautiful buildings in town. Right in the city centre. Features an elegant early 20th-century façade and spacious, highly-rated rooms. Easy walking access to nearby restaurants, bars, and the Cathedral.",
        link: "https://www.abgranhotel.com/"
    },
    {
        id: "parador",
        name: "PARADOR DE ALBACETE",
        stars: "****",
        description: "Spanish old style hotel outside of town. It has a pool, great if you want to chill having a drink by the pool, 15min taxi from city centre.",
        link: "https://parador.es/es/paradores/parador-de-albacete"
    },
    {
        id: "europa",
        name: "HOTEL EUROPA",
        stars: "***",
        description: "In city centre. A smart, family-friendly hotel known for its excellent value and secure on-site parking. Guests appreciate the quiet rooms and great buffet breakfast.",
        link: "https://www.hoteleuropaalbacete.es/"
    },
    {
        id: "florida",
        name: "HOTEL FLORIDA",
        stars: "***",
        description: "In city centre. Located next to the Bullring with an on-site restaurant serving popular homemade Mediterranean dishes. Offers comfortable rooms and friendly service.",
        link: "https://www.hotelflorida.es/"
    },
    {
        id: "san_antonio",
        name: "HOTEL SAN ANTONIO",
        stars: "****",
        description: "In city centre. Modern hotel situated right next to the Cathedral, featuring comfy beds and spacious bathrooms. Highly praised for its prime location and helpful staff.",
        link: "https://www.hotelsanantonio.es/"
    },
    {
        id: "universidad",
        name: "HOTEL UNIVERSIDAD",
        stars: "***",
        description: "Walking distance to city centre. Set in a quiet residential area near the university, offering easy parking and pet-friendly rooms. Known for its functional design and good breakfast.",
        link: "https://www.hoteluniversidad.com/en/"
    },
    {
        id: "urbanlux",
        name: "APARTAMENTOS URBANLUX",
        description: "Several locations around town. They fit 4 to 6 people. Modern, fully-equipped apartments with contactless check-in, praised for their cleanliness. Great for groups and families, with pet-friendly options available. Email info@urbanlux-apartamentos.com for more info.",
        link: "https://www.urbanlux-apartamentos.com/"
    }
];
export const AccommodationList: React.FC = () => {
    const { t, i18n } = useTranslation();
    const isSpanish = i18n.language === 'es';

    return (
        <section id="accommodation" className="relative w-full bg-[#eee8dc] text-[#4A3728] overflow-hidden pt-24 pb-32">
            <div className="shell max-w-7xl mx-auto px-4 flex flex-col items-center text-center">
                
                {/* Header Image with Rotation */}
                <div className={`relative w-full ${isSpanish ? 'max-w-[700px]' : 'max-w-[600px]'} mb-12`}>
                    <img
                        src={t('accommodation.title_svg')}
                        alt="Where to stay"
                        className="w-full h-auto"
                    />
                </div>

                {/* Description Paragraph */}
                <p className="font-sans text-[13px] sm:text-[15px] text-[#2c2c2c] max-w-[800px] mb-8 leading-snug tracking-wide whitespace-pre-wrap mx-auto">
                    {t('accommodation.description')}
                </p>

                {/* Google Maps Link */}
                <a href="https://maps.app.goo.gl/89jQzTB8AwocKGYz7" target="_blank" rel="noopener noreferrer" className="font-display font-bold text-[13px] sm:text-base tracking-[0.15em] text-[#a67c52] underline mb-10 hover:text-[#84391c] uppercase transition-colors">
                    {t('accommodation.google_maps')}
                </a>

                <div className="mb-12 w-full flex justify-center">
                    <img
                        src="/images/key.svg"
                        alt="Keys"
                        className="w-full max-w-[175px] h-auto object-contain"
                    />
                </div>

                {/* Accommodation List */}
                <div className="w-full flex flex-col gap-10">
                    {accommodations.map((item, idx) => (
                        <div key={idx} className="w-full text-left max-w-[800px] mx-auto">
                            {item.link ? (
                                <a href={item.link} target="_blank" rel="noopener noreferrer" className="inline-block hover:opacity-75 transition-opacity">
                                    <h3 className="font-display font-medium text-[18px] sm:text-[20px] text-[#84391c] tracking-[0.08em] uppercase mb-2 inline-block border-b-2 border-[#84391c] pb-0.5" style={{ letterSpacing: '0.12em' }}>
                                        {t(`accommodation.hotels.${item.id}.name`)} {item.stars && <span className="ml-[2px]">{item.stars}</span>}
                                    </h3>
                                </a>
                            ) : (
                                <h3 className="font-display font-medium text-[18px] sm:text-[20px] text-[#84391c] tracking-[0.08em] uppercase mb-2 inline-block border-b-2 border-[#84391c] pb-0.5" style={{ letterSpacing: '0.05em' }}>
                                    {t(`accommodation.hotels.${item.id}.name`)} {item.stars && <span className="ml-[2px]">{item.stars}</span>}
                                </h3>
                            )}
                            <p className="font-sans text-[13px] sm:text-[15px] text-[#4a4a4a] leading-snug mt-2">
                                {t(`accommodation.hotels.${item.id}.description`)}
                                {item.contact && (
                                    <>
                                        {" "}
                                        <a href={`mailto:${item.contact}`} className="underline hover:text-[#a67c52] transition-colors whitespace-nowrap">{item.contact}</a>
                                    </>
                                )}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};
